import Axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  message,
  Typography,
  Select,
  Modal,
  Space,
  Spin,
  Tree,
  TreeSelect,
  Tabs,
  Radio,
  DatePicker,
  Input,
  Checkbox,
  Alert,
  Tooltip,
  AutoComplete,
} from 'antd';
import {
  PlusOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  DragOutlined,
  StopOutlined,
  MergeCellsOutlined,
  UploadOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  BankOutlined,
} from '@ant-design/icons';
import Dropzone from 'react-dropzone';
import Papa from 'papaparse';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import {
  PageContainer,
  PageHeader,
  SectionCard,
  StatusBadge,
  EmptyState,
} from '../../common';
import {
  decodeCsvBuffer,
  validateCsvHeaders,
  formatHeaderValidationError,
  extractAxiosErrorMessage,
} from '../../../utils/csvUploadHelper';
import { downloadRosterTemplate } from '../../../utils/orgRosterHelper';
import { orgUnitsToCompanyNames } from '../../../utils/orgReportFilter';
// 系列会社一覧（有効な確定組織表の level 0 → 無ければ系列会社マスタ）: 新規バージョンへの取り込み・「系列会社を追加」の候補に使う
import { fetchCompanyRule } from '../../../utils/companyRule';

const { Text } = Typography;
const { confirm } = Modal;

// ─── 定数 ─────────────────────────────────────────────
// 所属① 〜 所属⑦ の全角丸数字（CSV ヘッダーで使用）
const CIRCLES = ['①', '②', '③', '④', '⑤', '⑥', '⑦'];
// CSV ヘッダー（所属①,…,所属⑦）
// ※ C′ 設計（2026-07）で ID 列（所属①ID 等）は廃止。CSV は名前列のみを扱う
//   （承継は「同じ経路・同じ名前」の一致でサーバーが自動判定するため ID 列は不要）。
const CSV_HEADERS = CIRCLES.map((c) => `所属${c}`);
// 系列会社ありグループ（「グループ会社・系列会社構造」PJ）の CSV は先頭に「会社名」列を持つ
// （会社名 = 組織表の最上位＝系列会社ノード（level 0）。サーバーは rows の company で受け取る）
const COMPANY_HEADER = '会社名';
const AFFILIATE_CSV_HEADERS = [COMPANY_HEADER, ...CSV_HEADERS];
// 版・組織単位の実行者トークン（tokenベース認可）
const executorToken = () => localStorage.getItem('w_auth');

// ルート（最上位）を示す TreeSelect の特別値
const ROOT_VALUE = '__root__';

// 組織ノードの内部キー（lineage_id = 非表示 ObjectId 文字列。画面には表示しない）。
// ツリー・マップ・D&D・移動/統合の同一性判定はすべてこの文字列キーで統一する。
const lid = (u) => (u ? String(u.lineage_id) : u);

// 日付を YYYY-MM-DD 表示に整形
const fmtDate = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : '');

/**
 * OrgMgrPage — 組織情報管理（/orgmgr）
 * ------------------------------------------------------------------
 * 企業（グループ）の組織表を時系列バージョンで管理する画面。
 * 組織の識別は内部の lineage_id（非表示）で行い、画面にはコードを表示しない（C′ 設計）。
 * role 2/3/9 が版を作成・編集・確定し、組織ツリーを編集する（Ph.2）。
 */
function OrgMgrPage(props) {
  const usr = useSelector((state) => state.user);
  const role = usr && usr.userData ? usr.userData.role : undefined;
  const myGroupCode = usr && usr.userData ? usr.userData.groupCode : undefined;

  // ─── 企業（グループ）選択 ───
  const [groups, setGroups] = useState([]);
  const [groupCode, setGroupCode] = useState('');

  // ─── バージョン ───
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);

  // ─── 名簿テンプレート用の有効確定バージョン（今日時点） ───
  // rosterVersion: null=有効確定バージョンなし / object=有効バージョンあり
  const [rosterVersion, setRosterVersion] = useState(null);
  const [rosterUnits, setRosterUnits] = useState([]);

  // ─── 組織単位（ユニット） ───
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [treeSelectedKey, setTreeSelectedKey] = useState(null);
  const [listFilter, setListFilter] = useState('');
  const [activeTab, setActiveTab] = useState('tree');

  // ─── 新規バージョン作成モーダル ───
  const [newVerOpen, setNewVerOpen] = useState(false);
  const [verMode, setVerMode] = useState('empty'); // 'empty' | 'copy' | 'csv'
  const [verName, setVerName] = useState('');
  const [verDate, setVerDate] = useState(null); // dayjs
  const [verNote, setVerNote] = useState('');
  const [verSourceId, setVerSourceId] = useState(undefined);
  const [verSubmitting, setVerSubmitting] = useState(false);
  // 系列会社ありグループ: 空の組織表から作成するとき系列会社一覧を最上位ノードとして取り込むか（既定 ON）と、その候補名
  const [verSeedCompanies, setVerSeedCompanies] = useState(true);
  const [companyNames, setCompanyNames] = useState([]);

  // ─── バージョンメタ編集モーダル ───
  const [metaOpen, setMetaOpen] = useState(false);
  const [metaTarget, setMetaTarget] = useState(null);
  const [metaName, setMetaName] = useState('');
  const [metaDate, setMetaDate] = useState(null);
  const [metaNote, setMetaNote] = useState('');
  const [metaSubmitting, setMetaSubmitting] = useState(false);

  // ─── 組織単位 操作モーダル（追加/名称変更/移動/統合） ───
  // mode: 'addChild' | 'addRoot' | 'rename' | 'move' | 'merge'
  const [unitModalOpen, setUnitModalOpen] = useState(false);
  const [unitModalMode, setUnitModalMode] = useState('addChild');
  const [unitModalTarget, setUnitModalTarget] = useState(null); // 対象ユニット（addRoot は null）
  const [unitInputName, setUnitInputName] = useState('');
  const [unitInputParent, setUnitInputParent] = useState(undefined); // move 先
  const [unitInputMerge, setUnitInputMerge] = useState(undefined); // 統合先
  const [unitSubmitting, setUnitSubmitting] = useState(false);
  // ツリードラッグ&ドロップ再配置の送信中フラグ
  const [treeDndSubmitting, setTreeDndSubmitting] = useState(false);

  // ─── CSV アップロードモーダル ───
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvRows, setCsvRows] = useState(null); // [{d1,…,d7}]（ID 列は持たない）
  // ─── CSV プレビュー：消滅↔新設のペアリング（改名指定） ───
  // pairings[old_lineage_id] = new_path（消滅ノードを新設 path と 1:1 で紐付け）
  const [csvPairings, setCsvPairings] = useState({});
  const [csvFileName, setCsvFileName] = useState('');
  const [csvPreview, setCsvPreview] = useState(null);
  // ペアリング前（pairings 無し）の基準プレビュー。消滅↔新設の候補列挙（Select 選択肢）に使う。
  const [csvBasePreview, setCsvBasePreview] = useState(null);
  const [csvName, setCsvName] = useState('');
  const [csvDate, setCsvDate] = useState(null);
  const [csvNote, setCsvNote] = useState('');
  const [csvBaseVersionId, setCsvBaseVersionId] = useState(undefined);
  // プレビュー（自動実行）が失敗した時だけ再試行 UI を出すためのフラグ
  const [csvPreviewFailed, setCsvPreviewFailed] = useState(false);
  const [csvSubmitting, setCsvSubmitting] = useState(false);
  // 表記ゆれ警告（プレビュー応答の warnings / truncated）
  const [csvWarnings, setCsvWarnings] = useState(null);
  const [csvTruncated, setCsvTruncated] = useState(false);

  // ─── CSV ダウンロード（廃止除外） ───
  const [downloadExcludeAbolished, setDownloadExcludeAbolished] =
    useState(false);

  const isRoleFixed = role === 3; // role 3 は自社グループ固定
  const canConfirm = role === 2 || role === 9; // 確定は role 2/9 のみ

  // 選択中グループが系列会社ありか（「グループ会社・系列会社構造」PJ）。
  // true のとき組織表の最上位ノード（level 0）は系列会社。false / undefined のグループは従来どおり
  const isAffiliate = useMemo(() => {
    const g = groups.find((x) => x.group_code === groupCode);
    return !!g && g.has_affiliates === true;
  }, [groups, groupCode]);
  // 系列会社一覧を取得（系列会社ありグループのみ。失敗・系列会社なしは []）
  const loadCompanyNames = async () => {
    if (!isAffiliate || !groupCode) {
      setCompanyNames([]);
      return [];
    }
    const rule = await fetchCompanyRule(groupCode);
    const names = rule && rule.enabled ? rule.names || [] : [];
    setCompanyNames(names);
    return names;
  };
  // 「系列会社を追加」の候補: 系列会社一覧のうち、この draft にまだ無い会社（level 0 ノード名と比較）
  const companyAddOptions = useMemo(() => {
    if (!isAffiliate) return [];
    const existing = new Set(
      units.filter((u) => u.level === 0).map((u) => String(u.name || '').trim())
    );
    return companyNames
      .filter((n) => !existing.has(String(n).trim()))
      .map((n) => ({ value: n }));
  }, [isAffiliate, units, companyNames]);
  // 系列会社（level 0）ノードか（系列会社ありグループのみ true になり得る）
  const isCompanyNode = (u) => isAffiliate && !!u && u.level === 0;
  // CSV のヘッダー（系列会社ありグループは「会社名」列付き）
  const csvHeaders = isAffiliate ? AFFILIATE_CSV_HEADERS : CSV_HEADERS;

  // ─── 初期ロード：グループ一覧取得 + 初期選択 ───
  useEffect(() => {
    const init = async () => {
      if (!usr.userData) return;
      try {
        const res = await Axios.get('/api/group/');
        const list = res.data && res.data.groups ? res.data.groups : [];
        setGroups(list);
        if (isRoleFixed) {
          // role 3：自社グループを固定選択
          if (myGroupCode) setGroupCode(myGroupCode);
        } else {
          // role 9/2：クエリ ?group= があれば初期選択
          const params = new URLSearchParams(
            (props.location && props.location.search) || ''
          );
          const qGroup = params.get('group');
          if (qGroup && list.find((g) => g.group_code === qGroup)) {
            setGroupCode(qGroup);
          }
        }
      } catch (error) {
        message.error(
          extractAxiosErrorMessage(error, 'グループ情報の取得に失敗しました。')
        );
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usr.userData]);

  // ─── グループ選択時：バージョン一覧 + 有効確定バージョン（名簿テンプレート用）をロード ───
  useEffect(() => {
    if (groupCode) {
      loadVersions(groupCode);
      loadRosterTree(groupCode);
    } else {
      setVersions([]);
      setSelectedVersion(null);
      setUnits([]);
      setRosterVersion(null);
      setRosterUnits([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupCode]);

  // 今日時点の有効確定バージョン + そのユニットを取得（名簿テンプレート用）。
  // 有効バージョンが無ければ rosterVersion=null（テンプレートボタンは無効化）。
  const loadRosterTree = async (gCode) => {
    try {
      const res = await Axios.get(`/api/org/tree/${gCode}`);
      if (res.data && res.data.success) {
        setRosterVersion(res.data.version || null);
        setRosterUnits(res.data.units || []);
        return;
      }
    } catch (error) {
      // 取得失敗はテンプレートボタンが無効になるだけ（他機能に影響させない）
      console.log({ error });
    }
    setRosterVersion(null);
    setRosterUnits([]);
  };

  // 名簿テンプレート（Excel）をダウンロード
  const downloadRoster = async () => {
    if (!rosterVersion) return;
    try {
      if (isAffiliate) {
        // 系列会社ありグループ: 所属フルパスは「会社名／…」、会社名列は系列会社名のドロップダウン
        await downloadRosterTemplate(rosterUnits, groupCode, {
          affiliate: true,
          companyNames: orgUnitsToCompanyNames(rosterUnits),
        });
      } else {
        await downloadRosterTemplate(rosterUnits, groupCode);
      }
    } catch (error) {
      console.log({ error });
      message.error('テンプレートの生成に失敗しました。');
    }
  };

  const groupName = useMemo(() => {
    const g = groups.find((x) => x.group_code === groupCode);
    return g ? g.group_name : groupCode;
  }, [groups, groupCode]);

  // バージョン一覧を取得（effective_from 降順で返る）
  const loadVersions = async (gCode) => {
    setVersionsLoading(true);
    try {
      const res = await Axios.get(`/api/org/versions/${gCode}`);
      const list = res.data && res.data.versions ? res.data.versions : [];
      setVersions(list);
      // 選択中バージョンを最新情報へ更新（削除されていれば選択解除）
      if (selectedVersion) {
        const still = list.find((v) => v._id === selectedVersion._id);
        if (still) {
          setSelectedVersion(still);
        } else {
          setSelectedVersion(null);
          setUnits([]);
        }
      }
    } catch (error) {
      message.error(
        extractAxiosErrorMessage(error, 'バージョン一覧の取得に失敗しました。')
      );
    } finally {
      setVersionsLoading(false);
    }
  };

  // 組織単位を取得
  const loadUnits = async (version) => {
    if (!version) return;
    setUnitsLoading(true);
    try {
      const res = await Axios.get(
        `/api/org/versions/${groupCode}/${version._id}/units`
      );
      setUnits(res.data && res.data.units ? res.data.units : []);
      setTreeSelectedKey(null);
    } catch (error) {
      message.error(
        extractAxiosErrorMessage(error, '組織情報の取得に失敗しました。')
      );
    } finally {
      setUnitsLoading(false);
    }
  };

  // バージョンを編集対象として選択
  const selectVersion = (version) => {
    setSelectedVersion(version);
    setActiveTab('tree');
    loadUnits(version);
  };

  // ─── バージョンの有効期間を算出 ───
  // このバージョンの effective_from 〜 次の確定バージョンの前日。最新は「現在有効」/「予定」。
  const periodText = (v) => {
    if (v.status !== 'confirmed') return '—';
    const start = fmtDate(v.effective_from);
    const later = versions
      .filter(
        (x) =>
          x.status === 'confirmed' &&
          dayjs(x.effective_from).isAfter(dayjs(v.effective_from))
      )
      .sort((a, b) =>
        dayjs(a.effective_from).isAfter(dayjs(b.effective_from)) ? 1 : -1
      );
    if (later.length > 0) {
      const end = dayjs(later[0].effective_from).subtract(1, 'day');
      return `${start} 〜 ${end.format('YYYY-MM-DD')}`;
    }
    const isFuture = dayjs(v.effective_from).isAfter(dayjs(), 'day');
    return `${start} 〜 ${isFuture ? '（予定）' : '現在有効'}`;
  };

  // ─── 新規バージョン作成 ───
  // グループに確定済みバージョンが存在するか（作成方法の既定選択・警告表示に使用）
  const hasConfirmedVersion = versions.some((v) => v.status === 'confirmed');

  const openNewVersion = () => {
    // 確定済みバージョンがあれば「既存バージョンをコピーして作成」を既定に、無ければ「空の組織表から作成」
    setVerMode(hasConfirmedVersion ? 'copy' : 'empty');
    setVerName('');
    setVerDate(null);
    setVerNote('');
    // コピー既定時は最新の確定済みバージョンをコピー元の初期値にする
    const latestConfirmed = versions.find((v) => v.status === 'confirmed');
    setVerSourceId(
      hasConfirmedVersion && latestConfirmed ? latestConfirmed._id : undefined
    );
    setVerSeedCompanies(true);
    if (isAffiliate) loadCompanyNames();
    setNewVerOpen(true);
  };

  const submitNewVersion = async () => {
    // CSV から作成の場合は CSV モーダルへ誘導
    if (verMode === 'csv') {
      setNewVerOpen(false);
      openCsvModal();
      return;
    }
    if (!verName.trim()) {
      message.error('バージョン名を入力してください。');
      return;
    }
    if (!verDate) {
      message.error('有効開始日を入力してください。');
      return;
    }
    if (verMode === 'copy' && !verSourceId) {
      message.error('コピー元のバージョンを選択してください。');
      return;
    }
    setVerSubmitting(true);
    try {
      const body = {
        executorToken: executorToken(),
        name: verName.trim(),
        effective_from: verDate.format('YYYY-MM-DD'),
        note: verNote.trim() || undefined,
        mode: verMode, // 'empty' | 'copy'
      };
      if (verMode === 'copy') body.source_version_id = verSourceId;
      // 系列会社ありグループ・空から作成: 系列会社一覧を最上位ノードとして取り込む（既定 ON）
      if (verMode === 'empty' && isAffiliate) body.seed_companies = verSeedCompanies;
      const res = await Axios.post(`/api/org/versions/${groupCode}`, body);
      if (res.data && res.data.success) {
        const seeded = Number(res.data.seeded_units) || 0;
        message.success(
          seeded > 0
            ? `新しいバージョンを作成し、系列会社 ${seeded} 社を最上位ノードとして取り込みました。`
            : '新しいバージョンを作成しました。'
        );
        setNewVerOpen(false);
        await loadVersions(groupCode);
        if (res.data.version) selectVersion(res.data.version);
      } else {
        throw new Error('バージョンの作成に失敗しました。');
      }
    } catch (error) {
      message.error(
        extractAxiosErrorMessage(error, 'バージョンの作成に失敗しました。')
      );
    } finally {
      setVerSubmitting(false);
    }
  };

  // ─── バージョンメタ編集 ───
  const openMeta = (version) => {
    setMetaTarget(version);
    setMetaName(version.name || '');
    setMetaDate(version.effective_from ? dayjs(version.effective_from) : null);
    setMetaNote(version.note || '');
    setMetaOpen(true);
  };

  const submitMeta = async () => {
    if (!metaName.trim()) {
      message.error('バージョン名を入力してください。');
      return;
    }
    if (!metaDate) {
      message.error('有効開始日を入力してください。');
      return;
    }
    setMetaSubmitting(true);
    try {
      const res = await Axios.patch(
        `/api/org/versions/${groupCode}/${metaTarget._id}`,
        {
          executorToken: executorToken(),
          name: metaName.trim(),
          effective_from: metaDate.format('YYYY-MM-DD'),
          note: metaNote.trim(),
        }
      );
      if (res.data && res.data.success) {
        message.success('バージョン情報を更新しました。');
        setMetaOpen(false);
        await loadVersions(groupCode);
      } else {
        throw new Error('更新に失敗しました。');
      }
    } catch (error) {
      message.error(extractAxiosErrorMessage(error, '更新に失敗しました。'));
    } finally {
      setMetaSubmitting(false);
    }
  };

  // ─── バージョン確定（role 2/9 のみ） ───
  const confirmVersion = (version) => {
    confirm({
      title: 'このバージョンを確定しますか？',
      icon: <ExclamationCircleOutlined />,
      content:
        '確定すると編集できなくなります。この日付以降の有効な組織表として使用されます。',
      okText: '確定する',
      cancelText: 'キャンセル',
      onOk: async () => {
        try {
          const res = await Axios.post(
            `/api/org/versions/${groupCode}/${version._id}/confirm`,
            { executorToken: executorToken() }
          );
          if (res.data && res.data.success) {
            message.success('バージョンを確定しました。');
            await loadVersions(groupCode);
            loadRosterTree(groupCode);
          } else {
            throw new Error('確定に失敗しました。');
          }
        } catch (error) {
          // 400 の場合は details 配列を一覧表示
          const details =
            error.response &&
            error.response.data &&
            error.response.data.details;
          if (Array.isArray(details) && details.length > 0) {
            Modal.error({
              title: '確定できませんでした',
              content: (
                <ul style={{ paddingLeft: 'var(--space-5)', margin: 0 }}>
                  {details.map((d, i) => (
                    <li key={i}>{typeof d === 'string' ? d : JSON.stringify(d)}</li>
                  ))}
                </ul>
              ),
            });
          } else {
            message.error(
              extractAxiosErrorMessage(error, '確定に失敗しました。')
            );
          }
        }
      },
    });
  };

  // ─── 確定解除（confirmed → draft、role 2/9 のみ） ───
  // 確定解除の実行（Ph.4c: 参照シーズンありの場合は 409 が返り、影響一覧の再確認モーダルを経て force 再実行）
  const runUnconfirm = async (version, force) => {
    try {
      const res = await Axios.post(
        `/api/org/versions/${groupCode}/${version._id}/unconfirm`,
        force
          ? { executorToken: executorToken(), force: true }
          : { executorToken: executorToken() }
      );
      if (res.data && res.data.success) {
        message.success('バージョンを編集状態に戻しました。');
        await loadVersions(groupCode);
        loadRosterTree(groupCode);
        // 選択中バージョンならツリーも再読込（編集ツールバーを反映）
        if (selectedVersion && selectedVersion._id === version._id) {
          loadUnits(version);
        }
      } else {
        throw new Error('編集状態への変更に失敗しました。');
      }
    } catch (error) {
      // 参照シーズンガード（409）: 影響シーズン一覧を表示して再確認 → force で再実行
      const data = error.response && error.response.data;
      if (
        error.response &&
        error.response.status === 409 &&
        data &&
        Array.isArray(data.affected_seasons)
      ) {
        confirm({
          title: 'この組織表を参照しているシーズンがあります',
          icon: <ExclamationCircleOutlined />,
          content: (
            <div>
              <p>
                以下のシーズンのレポート・名簿検証は、この組織表を基準にしています。
                解除すると部署一覧は従来方式（Group.department）に戻ります。
              </p>
              <ul style={{ paddingLeft: 'var(--space-5)', margin: 0 }}>
                {data.affected_seasons.map((se, i) => (
                  <li key={i}>{se.season_name}</li>
                ))}
              </ul>
            </div>
          ),
          okText: 'それでも編集に戻す',
          okButtonProps: { danger: true },
          cancelText: 'キャンセル',
          onOk: () => runUnconfirm(version, true),
        });
        return;
      }
      message.error(
        extractAxiosErrorMessage(error, '編集状態への変更に失敗しました。')
      );
    }
  };

  const unconfirmVersion = (version) => {
    confirm({
      title: 'このバージョンを編集状態に戻しますか？',
      icon: <ExclamationCircleOutlined />,
      content:
        '解除中はこのバージョンが有効な組織表から外れ、直前の確定バージョンが有効になります。誤字修正など軽微な変更に使用し、組織改編は「既存バージョンをコピーして作成」で新しいバージョンとして作成してください。',
      okText: '編集に戻す',
      cancelText: 'キャンセル',
      onOk: () => runUnconfirm(version, false),
    });
  };

  // ─── バージョン削除（draft のみ） ───
  const deleteVersion = (version) => {
    confirm({
      title: 'このバージョンを削除しますか？',
      icon: <ExclamationCircleOutlined />,
      content: `「${version.name}」を削除します。この操作は取り消せません。`,
      okText: '削除する',
      okButtonProps: { danger: true },
      cancelText: 'キャンセル',
      onOk: async () => {
        try {
          const res = await Axios.post(
            `/api/org/versions/${groupCode}/${version._id}/delete`,
            { executorToken: executorToken() }
          );
          if (res.data && res.data.success) {
            message.success('バージョンを削除しました。');
            if (selectedVersion && selectedVersion._id === version._id) {
              setSelectedVersion(null);
              setUnits([]);
            }
            await loadVersions(groupCode);
            loadRosterTree(groupCode);
          } else {
            throw new Error('削除に失敗しました。');
          }
        } catch (error) {
          message.error(extractAxiosErrorMessage(error, '削除に失敗しました。'));
        }
      },
    });
  };

  // ─── 組織単位 操作モーダルを開く ───
  const openUnitModal = (mode, target) => {
    setUnitModalMode(mode);
    setUnitModalTarget(target || null);
    setUnitInputName(mode === 'rename' && target ? target.name : '');
    setUnitInputParent(undefined);
    setUnitInputMerge(undefined);
    // 「系列会社を追加」は系列会社一覧を候補に出す（draft に無い会社だけ。自由入力も可）
    if (mode === 'addRoot' && isAffiliate) loadCompanyNames();
    setUnitModalOpen(true);
  };

  const submitUnitModal = async () => {
    setUnitSubmitting(true);
    try {
      if (unitModalMode === 'addChild' || unitModalMode === 'addRoot') {
        if (!unitInputName.trim()) {
          message.error('組織名を入力してください。');
          setUnitSubmitting(false);
          return;
        }
        const body = {
          executorToken: executorToken(),
          name: unitInputName.trim(),
          parent_lineage_id:
            unitModalMode === 'addChild' && unitModalTarget
              ? lid(unitModalTarget)
              : null,
        };
        // sort_order は送信しない（サーバーが兄弟末尾へ自動配置。並び順はドラッグ&ドロップで変更）
        const addRes = await Axios.post(
          `/api/org/versions/${groupCode}/${selectedVersion._id}/units`,
          body
        );
        // サーバーが確定済みバージョンから (親+名前) 一致で既存組織の lineage を承継した場合は専用メッセージ
        if (addRes.data && addRes.data.inherited && addRes.data.unit) {
          message.success(
            '既存の組織を引き継ぎました（組織の履歴が維持されます）。'
          );
        } else {
          message.success('組織情報を更新しました。');
        }
        setUnitModalOpen(false);
        await loadUnits(selectedVersion);
        return;
      } else if (unitModalMode === 'rename') {
        if (!unitInputName.trim()) {
          message.error('組織名を入力してください。');
          setUnitSubmitting(false);
          return;
        }
        await Axios.patch(`/api/org/units/${unitModalTarget._id}`, {
          executorToken: executorToken(),
          name: unitInputName.trim(),
        });
      } else if (unitModalMode === 'move') {
        // 系列会社ありグループでは最上位（＝系列会社の並び）へは移動できないため、移動先の選択を必須にする
        if (isAffiliate && (!unitInputParent || unitInputParent === ROOT_VALUE)) {
          message.error('移動先の親組織を選択してください。');
          setUnitSubmitting(false);
          return;
        }
        await Axios.patch(`/api/org/units/${unitModalTarget._id}`, {
          executorToken: executorToken(),
          parent_lineage_id:
            unitInputParent === ROOT_VALUE || !unitInputParent
              ? null
              : unitInputParent,
        });
      } else if (unitModalMode === 'merge') {
        if (!unitInputMerge) {
          message.error('統合先の組織を選択してください。');
          setUnitSubmitting(false);
          return;
        }
        await Axios.patch(`/api/org/units/${unitModalTarget._id}`, {
          executorToken: executorToken(),
          status: 'merged',
          merged_into_lineage_id: unitInputMerge,
        });
      }
      message.success('組織情報を更新しました。');
      setUnitModalOpen(false);
      await loadUnits(selectedVersion);
    } catch (error) {
      message.error(
        extractAxiosErrorMessage(error, '組織情報の更新に失敗しました。')
      );
    } finally {
      setUnitSubmitting(false);
    }
  };

  // ─── 廃止（status='abolished'） ───
  const abolishUnit = (unit) => {
    confirm({
      title: 'この組織を廃止しますか？',
      icon: <ExclamationCircleOutlined />,
      content: `「${unit.name}」を廃止状態にします。`,
      okText: '廃止する',
      okButtonProps: { danger: true },
      cancelText: 'キャンセル',
      onOk: async () => {
        try {
          await Axios.patch(`/api/org/units/${unit._id}`, {
            executorToken: executorToken(),
            status: 'abolished',
          });
          message.success('組織を廃止しました。');
          await loadUnits(selectedVersion);
        } catch (error) {
          message.error(extractAxiosErrorMessage(error, '廃止に失敗しました。'));
        }
      },
    });
  };

  // ─── 削除（POST units/:id/delete） ───
  const deleteUnit = (unit) => {
    confirm({
      title: 'この組織を削除しますか？',
      icon: <ExclamationCircleOutlined />,
      content: `「${unit.name}」を削除します。この操作は取り消せません。`,
      okText: '削除する',
      okButtonProps: { danger: true },
      cancelText: 'キャンセル',
      onOk: async () => {
        try {
          await Axios.post(`/api/org/units/${unit._id}/delete`, {
            executorToken: executorToken(),
          });
          message.success('組織を削除しました。');
          await loadUnits(selectedVersion);
        } catch (error) {
          // サーバーのエラーメッセージ（子あり / 他バージョン使用中）をそのまま表示
          message.error(extractAxiosErrorMessage(error, '削除に失敗しました。'));
        }
      },
    });
  };

  // ─── ツリー構築 ───
  // lineage_id（文字列）→ ユニット
  const unitById = useMemo(() => {
    const m = new Map();
    units.forEach((u) => m.set(lid(u), u));
    return m;
  }, [units]);

  // 親 lineage_id → 子ユニット配列（sort_order 昇順）
  const childrenByParent = useMemo(() => {
    const map = {};
    units.forEach((u) => {
      const p = u.parent_lineage_id ? String(u.parent_lineage_id) : ROOT_VALUE;
      if (!map[p]) map[p] = [];
      map[p].push(u);
    });
    Object.keys(map).forEach((k) =>
      map[k].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    );
    return map;
  }, [units]);

  const renderNodeTitle = (u) => (
    <span>
      {/* 系列会社（level 0）ノードは会社アイコン + 「（系列会社）」を付けて部署と区別する */}
      {isCompanyNode(u) && (
        <BankOutlined style={{ marginRight: 'var(--space-1)' }} />
      )}
      {u.name}
      {isCompanyNode(u) && <Text type="secondary">（系列会社）</Text>}
      {u.status === 'abolished' && (
        <StatusBadge tone="error" style={{ marginLeft: 'var(--space-2)' }}>
          廃止
        </StatusBadge>
      )}
      {u.status === 'merged' && (
        <StatusBadge tone="warning" style={{ marginLeft: 'var(--space-2)' }}>
          統合
        </StatusBadge>
      )}
    </span>
  );

  const treeData = useMemo(() => {
    const build = (parentKey) =>
      (childrenByParent[parentKey] || []).map((u) => ({
        key: lid(u),
        title: renderNodeTitle(u),
        children: build(lid(u)),
      }));
    return build(ROOT_VALUE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childrenByParent, isAffiliate]);

  // 系列会社ありグループのドラッグ&ドロップ制約:
  //   - 系列会社（level 0）ノードはドラッグ不可
  //   - 最上位（系列会社の並び）へのドロップ不可（所属は必ずいずれかの系列会社の下）
  const nodeDraggable = (node) => !isCompanyNode(unitById.get(node.key));
  const allowTreeDrop = ({ dropNode, dropPosition }) => {
    const dropUnit = unitById.get(dropNode.key);
    if (!dropUnit) return false;
    // dropPosition: 0 = ノード上（子として）/ -1・1 = 兄弟間
    if (dropPosition === 0) return true;
    return !!dropUnit.parent_lineage_id;
  };

  // ─── ツリー ドラッグ&ドロップ再配置（draft のみ） ───
  // gap ドロップ（兄弟間）= その親配下での順序変更 / ノード上ドロップ = そのノードの子末尾へ移動（親変更）。
  // 影響を受ける兄弟全員の sort_order を 0..n で再付与し reorder API を 1 回呼び出す。
  const onTreeDrop = async (info) => {
    if (!selectedVersion) return;
    const dragKey = info.dragNode.key; // ドラッグ元の lineage_id
    const dropKey = info.node.key; // ドロップ先ノードの lineage_id
    // antd 慣例: info.node.pos（"0-1-2" 形式）末尾と dropPosition の差で相対位置を算出
    const dropPos = info.node.pos.split('-');
    const dropPosition =
      info.dropPosition - Number(dropPos[dropPos.length - 1]);

    const dragUnit = unitById.get(dragKey);
    const dropUnit = unitById.get(dropKey);
    if (!dragUnit || !dropUnit) return;
    // 系列会社ありグループ: 系列会社ノードの移動・最上位へのドロップは不可（allowDrop と同じ判定の安全弁）
    if (
      isAffiliate &&
      (isCompanyNode(dragUnit) || (info.dropToGap && !dropUnit.parent_lineage_id))
    ) {
      message.error('系列会社の並び（最上位）には移動できません。');
      return;
    }

    // 移動先の親 lineage_id（null = 最上位）を決定
    const newParentId = info.dropToGap
      ? (dropUnit.parent_lineage_id
          ? String(dropUnit.parent_lineage_id)
          : null) // 兄弟間 = dropNode と同じ親
      : dropKey; // ノード上 = dropNode の子

    // 対象親配下の現在の子（sort_order 昇順）から dragUnit を除外
    const parentKeyForMap = newParentId || ROOT_VALUE;
    const siblings = (childrenByParent[parentKeyForMap] || []).filter(
      (u) => lid(u) !== dragKey
    );

    if (!info.dropToGap) {
      // 子の先頭へ（antd の慣例: 展開中の親と最初の子の間のギャップへの
      // ドロップも dropToGap=false（=親ノード上）として報告されるため、
      // 「先頭挿入」がユーザーのジェスチャー（最初の子にしたい）と一致する。
      // 末尾へ置きたい場合は最後の子の下のギャップへドロップすればよい）
      siblings.unshift(dragUnit);
    } else {
      // dropNode の前（dropPosition === -1）/ 後（それ以外）へ挿入
      const dropIndex = siblings.findIndex((u) => lid(u) === dropKey);
      const insertIndex = dropPosition <= -1 ? dropIndex : dropIndex + 1;
      siblings.splice(
        insertIndex < 0 ? siblings.length : insertIndex,
        0,
        dragUnit
      );
    }

    // 影響を受ける兄弟全員の sort_order を 0..n で再付与
    const updates = siblings.map((u, idx) => ({
      lineage_id: lid(u),
      parent_lineage_id: newParentId,
      sort_order: idx,
    }));

    setTreeDndSubmitting(true);
    try {
      const res = await Axios.patch(
        `/api/org/versions/${groupCode}/${selectedVersion._id}/reorder`,
        { executorToken: executorToken(), updates }
      );
      if (res.data && res.data.success) {
        message.success('並び順を変更しました。');
        await loadUnits(selectedVersion);
      } else {
        throw new Error('並び順の変更に失敗しました。');
      }
    } catch (error) {
      // 自身・子孫への移動などサーバー 400 メッセージをそのまま表示
      message.error(
        extractAxiosErrorMessage(error, '並び順の変更に失敗しました。')
      );
    } finally {
      setTreeDndSubmitting(false);
    }
  };

  // 自分自身 + 子孫の lineage_id 集合（移動・統合の対象除外用）
  const collectDescendants = (nodeId) => {
    const rootId = String(nodeId);
    const set = new Set([rootId]);
    const stack = [rootId];
    while (stack.length > 0) {
      const parent = stack.pop();
      units.forEach((u) => {
        const pid = u.parent_lineage_id ? String(u.parent_lineage_id) : null;
        if (pid === parent && !set.has(lid(u))) {
          set.add(lid(u));
          stack.push(lid(u));
        }
      });
    }
    return set;
  };

  // TreeSelect 用データ（自身・子孫を disabled 化）。value は lineage_id、表示は名前のみ。
  const buildTreeSelectData = (disableSet) => {
    const build = (parentKey) =>
      (childrenByParent[parentKey] || []).map((u) => ({
        value: lid(u),
        title: u.name,
        disabled: disableSet.has(lid(u)),
        children: build(lid(u)),
      }));
    return build(ROOT_VALUE);
  };

  // ─── 経路（親チェーン名）を組み立て ───
  const buildPath = (u) => {
    const chain = [];
    let cur = u;
    const guard = new Set();
    while (cur && !guard.has(lid(cur))) {
      guard.add(lid(cur));
      chain.unshift(cur.name);
      cur = cur.parent_lineage_id
        ? unitById.get(String(cur.parent_lineage_id))
        : null;
    }
    return chain.join('／');
  };

  // ─── CSV アップロード ───
  const openCsvModal = () => {
    setCsvRows(null);
    setCsvFileName('');
    setCsvPreview(null);
    setCsvBasePreview(null);
    setCsvPairings({});
    setCsvName('');
    setCsvDate(null);
    setCsvNote('');
    setCsvBaseVersionId(undefined);
    setCsvWarnings(null);
    setCsvTruncated(false);
    setCsvOpen(true);
  };

  const onCsvDrop = (files) => {
    const file = files[0];
    if (!file) return;
    setCsvFileName(file.name);
    setCsvPreview(null);
    setCsvBasePreview(null);
    setCsvPairings({});
    const reader = new FileReader();
    reader.onload = (e) => {
      const unicodeString = decodeCsvBuffer(e.target.result);
      Papa.parse(unicodeString, {
        header: true,
        dynamicTyping: false,
        skipEmptyLines: true,
        complete: (results) => {
          const headers = (results.meta && results.meta.fields) || [];
          // 少なくとも 所属① 列の存在を検証（系列会社ありグループは 会社名 列の存在を検証）
          const v = validateCsvHeaders(headers, [
            isAffiliate ? COMPANY_HEADER : '所属①',
          ]);
          if (!v.ok) {
            message.error({
              content: formatHeaderValidationError(v),
              key: 'orgcsv',
              duration: 10,
            });
            setCsvRows(null);
            return;
          }
          // 各行を {d1,…,d7} へ変換（全列空の行は除外）
          // 系列会社ありグループは {company, d1,…,d7}（会社名だけで所属が空の行も有効）
          // ※ C′ 設計で ID 列は使わない。ファイルに 所属①ID 等があっても無視する（名前列のみ）。
          const rows = [];
          (results.data || []).forEach((rec) => {
            const o = {};
            let hasValue = false;
            if (isAffiliate) {
              const company = (rec[COMPANY_HEADER] || '').trim();
              o.company = company;
              if (company) hasValue = true;
            }
            CIRCLES.forEach((c, i) => {
              const name = (rec[`所属${c}`] || '').trim();
              o[`d${i + 1}`] = name;
              if (name) hasValue = true;
            });
            if (hasValue) rows.push(o);
          });
          if (rows.length === 0) {
            message.error('有効なデータ行がありません。CSV を確認してください。');
            setCsvRows(null);
            return;
          }
          setCsvRows(rows);
          message.success(`${rows.length} 行を読み込みました。`);
          // 読み込み後はそのままプレビュー（dry_run）まで自動実行する。
          // ※ setCsvRows は非同期反映のため、解析済みの rows を明示的に渡す。
          runCsvPreview({ rows });
        },
      });
    };
    reader.readAsArrayBuffer(file);
  };

  // ペアリング（object）を API 送信用の配列へ変換
  // { old_lineage_id: new_path } → [{ old_lineage_id, new_path }]（値が空のものは除外）
  const pairingsToArray = (obj) =>
    Object.entries(obj || {})
      .filter(([, newPath]) => newPath)
      .map(([old_lineage_id, new_path]) => ({ old_lineage_id, new_path }));

  // dry_run プレビューを取得（pairings を含めて送信）
  //   overrides: state 反映前の値を使う場合に指定（{ rows, baseVersionId }）
  //     - rows: ドロップ直後の自動プレビュー用（解析済み行）
  //     - baseVersionId: 基準バージョン変更直後の自動プレビュー用（クリア時の undefined も表現するため hasOwnProperty で判定）
  const fetchCsvPreview = async (pairings, overrides = {}) => {
    const baseVersionId = Object.prototype.hasOwnProperty.call(
      overrides,
      'baseVersionId'
    )
      ? overrides.baseVersionId
      : csvBaseVersionId;
    const res = await Axios.post(`/api/org/versions/${groupCode}/csv`, {
      executorToken: executorToken(),
      dry_run: true,
      rows: overrides.rows || csvRows,
      base_version_id: baseVersionId || undefined,
      pairings: pairingsToArray(pairings),
    });
    if (!(res.data && res.data.success)) {
      throw new Error('プレビューの取得に失敗しました。');
    }
    const preview = res.data.preview || {};
    // 表記ゆれ警告は preview 内 or トップレベルのどちらでも受け取れるようにする
    setCsvWarnings(res.data.warnings || preview.warnings || null);
    setCsvTruncated(res.data.truncated || preview.truncated || false);
    return preview;
  };

  // CSV プレビュー（基準プレビューの取得）: ファイルのドロップ時と基準バージョン変更時に自動実行する。
  //   overrides = { rows, baseVersionId }（state 反映前の値を使う場合に指定）
  const runCsvPreview = async (overrides = {}) => {
    const rows = overrides.rows || csvRows;
    if (!rows) {
      message.error('CSV ファイルをアップロードしてください。');
      return;
    }
    setCsvSubmitting(true);
    setCsvPreviewFailed(false);
    try {
      const preview = await fetchCsvPreview({}, overrides);
      setCsvPairings({});
      setCsvBasePreview(preview);
      setCsvPreview(preview);
    } catch (error) {
      setCsvPreviewFailed(true);
      message.error(
        extractAxiosErrorMessage(error, 'プレビューの取得に失敗しました。')
      );
    } finally {
      setCsvSubmitting(false);
    }
  };

  // ペアリングを反映してプレビューを更新（現在のプレビューのみ差し替え。基準プレビューは保持）
  const refreshCsvPreview = async (pairings) => {
    if (!csvRows) return;
    setCsvSubmitting(true);
    try {
      const preview = await fetchCsvPreview(pairings);
      setCsvPreview(preview);
    } catch (error) {
      message.error(
        extractAxiosErrorMessage(error, 'プレビューの更新に失敗しました。')
      );
    } finally {
      setCsvSubmitting(false);
    }
  };

  // 改名先ペアリングを変更（消滅ノード old_lineage_id ↔ 新設 new_path）→ 即座に再プレビュー
  const setPairing = (oldLineageId, newPath) => {
    const next = { ...csvPairings };
    if (newPath) next[oldLineageId] = newPath;
    else delete next[oldLineageId];
    setCsvPairings(next);
    refreshCsvPreview(next);
  };

  // CSV から本作成（dry_run=false）
  const runCsvCreate = async () => {
    if (!csvName.trim()) {
      message.error('バージョン名を入力してください。');
      return;
    }
    if (!csvDate) {
      message.error('有効開始日を入力してください。');
      return;
    }
    setCsvSubmitting(true);
    try {
      const res = await Axios.post(`/api/org/versions/${groupCode}/csv`, {
        executorToken: executorToken(),
        dry_run: false,
        name: csvName.trim(),
        effective_from: csvDate.format('YYYY-MM-DD'),
        note: csvNote.trim() || undefined,
        base_version_id: csvBaseVersionId || undefined,
        rows: csvRows,
        // 適用時も現在のペアリング（改名指定）を必ず送信する
        pairings: pairingsToArray(csvPairings),
      });
      if (res.data && res.data.success) {
        message.success('CSV からバージョンを作成しました。');
        setCsvOpen(false);
        await loadVersions(groupCode);
        if (res.data.version) selectVersion(res.data.version);
      } else {
        throw new Error('作成に失敗しました。');
      }
    } catch (error) {
      // 400 + errors の場合はエラー内容を表示
      const errs =
        error.response && error.response.data && error.response.data.errors;
      if (Array.isArray(errs) && errs.length > 0) {
        Modal.error({
          title: '作成できませんでした',
          content: (
            <ul style={{ paddingLeft: 'var(--space-5)', margin: 0 }}>
              {errs.map((e, i) => (
                <li key={i}>
                  {e.row != null ? `${e.row} 行目: ` : ''}
                  {e.message || JSON.stringify(e)}
                </li>
              ))}
            </ul>
          ),
        });
      } else {
        message.error(extractAxiosErrorMessage(error, '作成に失敗しました。'));
      }
    } finally {
      setCsvSubmitting(false);
    }
  };

  // サンプル組織 CSV（ヘッダー + 例示 3 行）を BOM 付き UTF-8 でダウンロード。
  // ※ C′ 設計で ID 列は無し（名前列のみ）。同じ経路・同じ名前なら自動で既存組織の履歴が引き継がれる。
  // ※ CSV アップロードモーダルと新規バージョン作成モーダルの 2 箇所から呼ぶ（生成ロジックは 1 箇所に集約）。
  const downloadSampleCsv = () => {
    // 例示 3 行（所属名のみ。系列会社ありグループは先頭に会社名）
    const sampleCompany = isAffiliate ? { [COMPANY_HEADER]: 'サンプル株式会社' } : {};
    const sampleRows = [
      { ...sampleCompany, '所属①': '大阪支店', '所属②': '物流部', '所属③': '商品グループ' },
      { ...sampleCompany, '所属①': '大阪支店', '所属②': '営業部' },
      { ...sampleCompany, '所属①': '関東支店', '所属②': '業務部' },
    ];
    const data = sampleRows.map((row) => {
      const o = {};
      csvHeaders.forEach((h) => {
        o[h] = row[h] || '';
      });
      return o;
    });
    const csv = Papa.unparse({ fields: csvHeaders, data });
    triggerDownload(csv, `組織情報サンプル.csv`);
  };

  // 選択バージョンの組織表を CSV でダウンロード（リーフの経路を展開）
  const downloadVersionCsv = () => {
    if (!selectedVersion || units.length === 0) {
      message.error('ダウンロードできる組織情報がありません。');
      return;
    }
    // 子を持たないユニット（リーフ）を抽出（親として参照されている lineage_id を除外）
    const parentIds = new Set(
      units
        .filter((u) => u.parent_lineage_id)
        .map((u) => String(u.parent_lineage_id))
    );
    let leaves = units.filter((u) => !parentIds.has(lid(u)));
    if (downloadExcludeAbolished) {
      leaves = leaves.filter((u) => u.status === 'active');
    }
    const rows = leaves.map((leaf) => {
      const chain = [];
      let cur = leaf;
      const guard = new Set();
      while (cur && !guard.has(lid(cur))) {
        guard.add(lid(cur));
        chain.unshift(cur);
        cur = cur.parent_lineage_id
          ? unitById.get(String(cur.parent_lineage_id))
          : null;
      }
      const o = {};
      // 系列会社ありグループ: 先頭の系列会社（level 0）ノードを「会社名」列へ、残りを所属①〜⑦へ
      let deptChain = chain;
      if (isAffiliate) {
        o[COMPANY_HEADER] = '';
        if (chain.length > 0 && isCompanyNode(chain[0])) {
          o[COMPANY_HEADER] = chain[0].name;
          deptChain = chain.slice(1);
        }
      }
      CIRCLES.forEach((c) => {
        o[`所属${c}`] = '';
      });
      deptChain.forEach((n, i) => {
        if (i < 7) {
          o[`所属${CIRCLES[i]}`] = n.name;
        }
      });
      return o;
    });
    const csv = Papa.unparse({ fields: csvHeaders, data: rows });
    triggerDownload(
      csv,
      `組織情報_${groupCode}_${selectedVersion.name}.csv`
    );
  };

  // BOM 付き UTF-8 でファイルダウンロードを発火
  const triggerDownload = (csvString, fileName) => {
    const blob = new Blob(['﻿' + csvString], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ─── バージョン一覧テーブル列 ───
  const versionColumns = [
    {
      title: 'バージョン名',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => selectVersion(record)}
        >
          {text}
        </Button>
      ),
    },
    {
      title: '有効開始日',
      dataIndex: 'effective_from',
      key: 'effective_from',
      render: (d) => fmtDate(d),
    },
    {
      title: '有効期間',
      key: 'period',
      render: (text, record) => periodText(record),
    },
    {
      title: '状態',
      dataIndex: 'status',
      key: 'status',
      render: (status) =>
        status === 'confirmed' ? (
          <StatusBadge tone="success">確定済み</StatusBadge>
        ) : (
          <StatusBadge tone="warning">編集中</StatusBadge>
        ),
    },
    {
      title: '作成日',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d) => fmtDate(d),
    },
    {
      title: '操作',
      key: 'action',
      render: (text, record) => {
        const isDraft = record.status === 'draft';
        return (
          <Space size="small" wrap>
            <Button size="small" onClick={() => selectVersion(record)}>
              選択
            </Button>
            {isDraft && canConfirm && (
              <Button
                size="small"
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => confirmVersion(record)}
              >
                確定
              </Button>
            )}
            {!isDraft && canConfirm && (
              <Button size="small" onClick={() => unconfirmVersion(record)}>
                編集に戻す
              </Button>
            )}
            {isDraft && (
              <Button size="small" onClick={() => openMeta(record)}>
                メタ編集
              </Button>
            )}
            {isDraft && (
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => deleteVersion(record)}
              >
                削除
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  // ─── 一覧ビュー用の行データ（フィルタ適用） ───
  const listRows = useMemo(() => {
    const kw = listFilter.trim().toLowerCase();
    const withPath = units.map((u) => ({
      ...u,
      _path: buildPath(u),
    }));
    if (!kw) return withPath;
    // C′ 設計でコードは非表示のため、名称のみで絞り込む
    return withPath.filter((u) => (u.name || '').toLowerCase().includes(kw));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, listFilter, unitById]);

  const isDraftSelected = selectedVersion && selectedVersion.status === 'draft';

  // 組織単位の状態バッジ
  const unitStatusBadge = (status) => {
    if (status === 'abolished')
      return <StatusBadge tone="error">廃止</StatusBadge>;
    if (status === 'merged')
      return <StatusBadge tone="warning">統合</StatusBadge>;
    return <StatusBadge tone="success">有効</StatusBadge>;
  };

  const listColumns = [
    { title: '組織名', dataIndex: 'name', key: 'name' },
    // 系列会社ありグループは level 0（系列会社）を「会社」と表示（経路 _path は先頭が系列会社名）
    isAffiliate
      ? {
          title: '階層',
          dataIndex: 'level',
          key: 'level',
          render: (level) => (level === 0 ? '会社' : level),
        }
      : { title: '階層', dataIndex: 'level', key: 'level' },
    { title: '経路', dataIndex: '_path', key: '_path' },
    {
      title: '状態',
      dataIndex: 'status',
      key: 'status',
      render: (status) => unitStatusBadge(status),
    },
    ...(isDraftSelected
      ? [
          {
            title: '操作',
            key: 'action',
            render: (text, record) => (
              <Space size="small" wrap>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => openUnitModal('rename', record)}
                >
                  名称変更
                </Button>
                {/* 系列会社（level 0）ノードは移動対象外 */}
                {!isCompanyNode(record) && (
                  <Button
                    size="small"
                    icon={<DragOutlined />}
                    onClick={() => openUnitModal('move', record)}
                  >
                    移動
                  </Button>
                )}
                {record.status === 'active' && (
                  <Button
                    size="small"
                    icon={<StopOutlined />}
                    onClick={() => abolishUnit(record)}
                  >
                    廃止
                  </Button>
                )}
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => deleteUnit(record)}
                >
                  削除
                </Button>
              </Space>
            ),
          },
        ]
      : []),
  ];

  // ツリーで選択中のユニット
  const treeSelectedUnit = treeSelectedKey
    ? unitById.get(treeSelectedKey)
    : null;

  // ─── レンダー ───
  return (
    <PageContainer fluid style={{ paddingTop: 'var(--space-6)' }}>
      <PageHeader
        title="組織情報管理"
        subtitle="企業の組織表を登録し、時系列バージョンで管理します。"
      />

      {/* 企業（グループ）選択 */}
      <SectionCard
        title="企業（グループ）選択"
        style={{ marginBottom: 'var(--space-6)' }}
      >
        {isRoleFixed ? (
          <div>
            <Text strong style={{ marginRight: 'var(--space-2)' }}>
              対象企業：
            </Text>
            <Text>{groupName || myGroupCode || '—'}</Text>
          </div>
        ) : (
          <Select
            showSearch
            style={{ minWidth: 320, maxWidth: '100%' }}
            placeholder="企業を選択してください"
            value={groupCode || undefined}
            optionFilterProp="label"
            onChange={(val) => setGroupCode(val)}
            options={groups.map((g) => ({
              label: `${g.group_name}（${g.group_code}）`,
              value: g.group_code,
            }))}
          />
        )}
      </SectionCard>

      {/* バージョンタイムライン */}
      {groupCode && (
        <SectionCard
          title="バージョンタイムライン"
          extra={
            <Space wrap>
              <Tooltip
                title={
                  rosterVersion
                    ? '名簿作成用の Excel テンプレート（所属はドロップダウン選択式）'
                    : '有効な確定バージョンがある場合にダウンロードできます'
                }
              >
                {/* 無効時もツールチップを表示するため span でラップ */}
                <span>
                  <Button
                    icon={<DownloadOutlined />}
                    disabled={!rosterVersion}
                    onClick={downloadRoster}
                  >
                    名簿テンプレート
                  </Button>
                </span>
              </Tooltip>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openNewVersion}
              >
                新規バージョン作成
              </Button>
            </Space>
          }
          style={{ marginBottom: 'var(--space-6)' }}
        >
          <Spin spinning={versionsLoading}>
            {versions.length === 0 ? (
              <EmptyState message="この企業のバージョンはまだありません。「新規バージョン作成」から作成してください。" />
            ) : (
              <Table
                columns={versionColumns}
                dataSource={versions}
                size="small"
                rowKey="_id"
                pagination={false}
                rowClassName={(record) =>
                  selectedVersion && selectedVersion._id === record._id
                    ? 'ant-table-row-selected'
                    : ''
                }
              />
            )}
          </Spin>
        </SectionCard>
      )}

      {/* 組織ツリー編集器 */}
      {selectedVersion && (
        <SectionCard
          title={`組織ツリー — ${selectedVersion.name}`}
          extra={
            <Space wrap>
              <Checkbox
                checked={downloadExcludeAbolished}
                onChange={(e) => setDownloadExcludeAbolished(e.target.checked)}
              >
                廃止を除外
              </Checkbox>
              <Button
                icon={<DownloadOutlined />}
                onClick={downloadVersionCsv}
              >
                CSV ダウンロード
              </Button>
            </Space>
          }
        >
          <Spin spinning={unitsLoading}>
            {isAffiliate && units.length > 0 && !units.some((u) => u.level === 0) && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <Text type="warning">
                  このバージョンには系列会社（最上位）ノードがありません。系列会社ありグループでは、系列会社ノードの無い組織表は部署の選択肢に使われず、各画面の部署一覧はユーザーの所属から作られます。系列会社ごとの組織表が必要な場合は、「会社名」列付きの
                  CSV から新しいバージョンを作成して確定してください。
                </Text>
              </div>
            )}
            {!isDraftSelected && (
              <div
                style={{
                  marginBottom: 'var(--space-4)',
                  color: 'var(--grey-65)',
                }}
              >
                <Text type="secondary">
                  このバージョンは確定済みのため、閲覧のみ可能です。修正が必要な場合は「編集に戻す」（管理者のみ）、組織改編は新しいバージョンの作成で行ってください。
                </Text>
              </div>
            )}

            {/* draft のみ：編集ツールバー */}
            {isDraftSelected && (
              <Space wrap style={{ marginBottom: 'var(--space-4)' }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => openUnitModal('addRoot', null)}
                >
                  {isAffiliate ? '系列会社を追加' : '最上位組織を追加'}
                </Button>
                <Button
                  icon={<PlusOutlined />}
                  disabled={!treeSelectedUnit}
                  onClick={() => openUnitModal('addChild', treeSelectedUnit)}
                >
                  {isCompanyNode(treeSelectedUnit) ? '所属を追加' : '子組織を追加'}
                </Button>
                <Button
                  icon={<EditOutlined />}
                  disabled={!treeSelectedUnit}
                  onClick={() => openUnitModal('rename', treeSelectedUnit)}
                >
                  名称変更
                </Button>
                <Button
                  icon={<DragOutlined />}
                  // 系列会社（level 0）ノードは移動対象外
                  disabled={!treeSelectedUnit || isCompanyNode(treeSelectedUnit)}
                  onClick={() => openUnitModal('move', treeSelectedUnit)}
                >
                  移動
                </Button>
                <Button
                  icon={<StopOutlined />}
                  disabled={
                    !treeSelectedUnit ||
                    (treeSelectedUnit && treeSelectedUnit.status !== 'active')
                  }
                  onClick={() => abolishUnit(treeSelectedUnit)}
                >
                  廃止
                </Button>
                <Button
                  icon={<MergeCellsOutlined />}
                  disabled={!treeSelectedUnit}
                  onClick={() => openUnitModal('merge', treeSelectedUnit)}
                >
                  統合
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!treeSelectedUnit}
                  onClick={() => deleteUnit(treeSelectedUnit)}
                >
                  削除
                </Button>
              </Space>
            )}

            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: 'tree',
                  label: 'ツリー表示',
                  children:
                    units.length === 0 ? (
                      <EmptyState message="この組織表にはまだ組織がありません。" />
                    ) : (
                      <Spin spinning={treeDndSubmitting}>
                        {isDraftSelected && (
                          <Text
                            type="secondary"
                            style={{
                              display: 'block',
                              marginBottom: 'var(--space-3)',
                            }}
                          >
                            ドラッグ＆ドロップで並び順・所属先を変更できます
                          </Text>
                        )}
                        <Tree
                          showLine
                          defaultExpandAll
                          draggable={
                            isDraftSelected
                              ? isAffiliate
                                ? { icon: false, nodeDraggable }
                                : { icon: false }
                              : false
                          }
                          allowDrop={
                            isDraftSelected && isAffiliate ? allowTreeDrop : undefined
                          }
                          onDrop={isDraftSelected ? onTreeDrop : undefined}
                          treeData={treeData}
                          selectedKeys={
                            treeSelectedKey ? [treeSelectedKey] : []
                          }
                          onSelect={(keys) =>
                            setTreeSelectedKey(keys.length ? keys[0] : null)
                          }
                        />
                      </Spin>
                    ),
                },
                {
                  key: 'list',
                  label: '一覧表示',
                  children: (
                    <div>
                      <Input.Search
                        allowClear
                        placeholder="名称で絞り込み"
                        style={{
                          maxWidth: 360,
                          marginBottom: 'var(--space-4)',
                        }}
                        value={listFilter}
                        onChange={(e) => setListFilter(e.target.value)}
                      />
                      {listRows.length === 0 ? (
                        <EmptyState message="該当する組織がありません。" />
                      ) : (
                        <Table
                          columns={listColumns}
                          dataSource={listRows}
                          size="small"
                          rowKey="_id"
                          pagination={{
                            // pageSize（制御値）ではなく defaultPageSize を使う。
                            // pageSize を渡すと antd が毎レンダーで内部状態を上書きし、
                            // 表示件数セレクタの変更が反映されなくなるため（他画面と同じ方式）
                            defaultPageSize: 50,
                            showSizeChanger: true,
                            pageSizeOptions: ['25', '50', '100', '200'],
                            hideOnSinglePage: true,
                          }}
                        />
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </Spin>
        </SectionCard>
      )}

      {/* 新規バージョン作成モーダル */}
      <Modal
        title="新規バージョン作成"
        open={newVerOpen}
        onOk={submitNewVersion}
        onCancel={() => setNewVerOpen(false)}
        okText={verMode === 'csv' ? 'CSV アップロードへ進む' : '作成'}
        cancelText="キャンセル"
        confirmLoading={verSubmitting}
        destroyOnHidden
      >
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Text strong style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
            作成方法
          </Text>
          <Radio.Group
            value={verMode}
            onChange={(e) => setVerMode(e.target.value)}
          >
            <Space direction="vertical">
              <Radio value="empty">空の組織表から作成</Radio>
              <Radio value="copy">既存バージョンをコピーして作成</Radio>
              <Radio value="csv">CSV から作成</Radio>
            </Space>
          </Radio.Group>
          {verMode === 'csv' && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <Button
                type="link"
                style={{ padding: 0 }}
                icon={<DownloadOutlined />}
                onClick={downloadSampleCsv}
              >
                サンプルCSVをダウンロード
              </Button>
            </div>
          )}
          {isAffiliate && verMode === 'empty' && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <Checkbox
                checked={verSeedCompanies}
                onChange={(e) => setVerSeedCompanies(e.target.checked)}
              >
                系列会社{companyNames.length > 0 ? `（${companyNames.length} 社: ${companyNames.join('・')}）` : ''}
                を最上位ノードとして取り込む
              </Checkbox>
              <Text type="secondary" style={{ display: 'block', marginTop: 'var(--space-1)' }}>
                系列会社マスタ（有効な確定組織表があればその系列会社）の会社を最上位に置いた状態から作成します。取り込まない場合は「系列会社を追加」で個別に追加します。
              </Text>
            </div>
          )}
          {hasConfirmedVersion && verMode === 'empty' && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 'var(--space-3)' }}
              message="確定済みバージョンがあるため、基本は「既存バージョンをコピーして作成」を推奨します。空から作成する場合も、同じ親・同じ名称の組織は既存の履歴が自動で引き継がれます。"
            />
          )}
        </div>

        {verMode === 'copy' && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <Text strong style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
              コピー元バージョン
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder="コピー元を選択"
              value={verSourceId}
              onChange={setVerSourceId}
              options={versions.map((v) => ({
                label: `${v.name}（${fmtDate(v.effective_from)}・${
                  v.status === 'confirmed' ? '確定済み' : '編集中'
                }）`,
                value: v._id,
              }))}
            />
          </div>
        )}

        {verMode !== 'csv' && (
          <>
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Text
                strong
                style={{ display: 'block', marginBottom: 'var(--space-2)' }}
              >
                バージョン名（必須）
              </Text>
              <Input
                value={verName}
                onChange={(e) => setVerName(e.target.value)}
                placeholder="例：2026年度 組織表"
              />
            </div>
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Text
                strong
                style={{ display: 'block', marginBottom: 'var(--space-2)' }}
              >
                有効開始日（必須）
              </Text>
              <DatePicker
                style={{ width: '100%' }}
                value={verDate}
                onChange={setVerDate}
              />
            </div>
            <div>
              <Text
                strong
                style={{ display: 'block', marginBottom: 'var(--space-2)' }}
              >
                備考
              </Text>
              <Input.TextArea
                rows={2}
                value={verNote}
                onChange={(e) => setVerNote(e.target.value)}
              />
            </div>
          </>
        )}

        {verMode === 'csv' && (
          <Text type="secondary">
            「CSV アップロードへ進む」を押すと、CSV アップロード画面に移動します。
          </Text>
        )}
      </Modal>

      {/* バージョンメタ編集モーダル */}
      <Modal
        title="バージョン情報の編集"
        open={metaOpen}
        onOk={submitMeta}
        onCancel={() => setMetaOpen(false)}
        okText="保存"
        cancelText="キャンセル"
        confirmLoading={metaSubmitting}
        destroyOnHidden
      >
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Text strong style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
            バージョン名（必須）
          </Text>
          <Input value={metaName} onChange={(e) => setMetaName(e.target.value)} />
        </div>
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Text strong style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
            有効開始日（必須）
          </Text>
          <DatePicker
            style={{ width: '100%' }}
            value={metaDate}
            onChange={setMetaDate}
          />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
            備考
          </Text>
          <Input.TextArea
            rows={2}
            value={metaNote}
            onChange={(e) => setMetaNote(e.target.value)}
          />
        </div>
      </Modal>

      {/* 組織単位 操作モーダル */}
      <Modal
        title={
          unitModalMode === 'addChild'
            ? isCompanyNode(unitModalTarget)
              ? '所属を追加'
              : '子組織を追加'
            : unitModalMode === 'addRoot'
            ? isAffiliate
              ? '系列会社を追加'
              : '最上位組織を追加'
            : unitModalMode === 'rename'
            ? '名称変更'
            : unitModalMode === 'move'
            ? '組織の移動'
            : '組織の統合'
        }
        open={unitModalOpen}
        onOk={submitUnitModal}
        onCancel={() => setUnitModalOpen(false)}
        okText="実行"
        cancelText="キャンセル"
        confirmLoading={unitSubmitting}
        destroyOnHidden
      >
        {(unitModalMode === 'addChild' ||
          unitModalMode === 'addRoot' ||
          unitModalMode === 'rename') && (
          <>
            {unitModalMode === 'addChild' && unitModalTarget && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <Text type="secondary">
                  親組織：{unitModalTarget.name}
                </Text>
              </div>
            )}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Text
                strong
                style={{ display: 'block', marginBottom: 'var(--space-2)' }}
              >
                {unitModalMode === 'addRoot' && isAffiliate ? '系列会社名（必須）' : '組織名（必須）'}
              </Text>
              {unitModalMode === 'addRoot' && isAffiliate ? (
                <>
                  {/* 系列会社一覧（この draft に無い会社）を候補に出し、新しい会社名の直接入力も受け付ける */}
                  <AutoComplete
                    style={{ width: '100%' }}
                    options={companyAddOptions}
                    value={unitInputName}
                    onChange={(v) => setUnitInputName(v || '')}
                    placeholder="系列会社一覧から選択、または新しい会社名を入力"
                  />
                  <Text type="secondary" style={{ display: 'block', marginTop: 'var(--space-1)' }}>
                    {companyAddOptions.length > 0
                      ? `未追加の系列会社 ${companyAddOptions.length} 社を候補に表示しています。`
                      : '系列会社一覧の会社はすべて追加済みです（新しい会社名は直接入力できます）。'}
                  </Text>
                </>
              ) : (
                <Input
                  value={unitInputName}
                  onChange={(e) => setUnitInputName(e.target.value)}
                />
              )}
            </div>
          </>
        )}

        {unitModalMode === 'move' && unitModalTarget && (
          <div>
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Text type="secondary">対象：{unitModalTarget.name}</Text>
            </div>
            <Text strong style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
              移動先の親組織
            </Text>
            {/* 系列会社ありグループ: 最上位（系列会社の並び）へは移動できない。移動先には系列会社（別の系列会社直下）も選べる */}
            <TreeSelect
              style={{ width: '100%' }}
              placeholder={
                isAffiliate ? '移動先を選択' : '移動先を選択（未選択で最上位）'
              }
              value={unitInputParent}
              onChange={setUnitInputParent}
              treeDefaultExpandAll
              allowClear
              treeData={
                isAffiliate
                  ? buildTreeSelectData(collectDescendants(lid(unitModalTarget)))
                  : [
                      { value: ROOT_VALUE, title: '（最上位）' },
                      ...buildTreeSelectData(collectDescendants(lid(unitModalTarget))),
                    ]
              }
            />
          </div>
        )}

        {unitModalMode === 'merge' && unitModalTarget && (
          <div>
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Text type="secondary">
                対象：{unitModalTarget.name} を統合します。
              </Text>
            </div>
            <Text strong style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
              統合先の組織（必須）
            </Text>
            <TreeSelect
              style={{ width: '100%' }}
              placeholder="統合先を選択"
              value={unitInputMerge}
              onChange={setUnitInputMerge}
              treeDefaultExpandAll
              treeData={buildTreeSelectData(collectDescendants(lid(unitModalTarget)))}
            />
          </div>
        )}
      </Modal>

      {/* CSV アップロードモーダル */}
      <Modal
        title="CSV から組織表を作成"
        open={csvOpen}
        onCancel={() => setCsvOpen(false)}
        width={720}
        destroyOnHidden
        footer={[
          <Button key="cancel" onClick={() => setCsvOpen(false)}>
            キャンセル
          </Button>,
          <Button
            key="create"
            type="primary"
            onClick={runCsvCreate}
            loading={csvSubmitting}
            disabled={
              !csvPreview ||
              (csvPreview.errors && csvPreview.errors.length > 0) ||
              !csvName.trim() ||
              !csvDate
            }
          >
            この内容で作成
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Button type="link" style={{ padding: 0 }} onClick={downloadSampleCsv}>
            サンプルCSVをダウンロード
          </Button>
          <Text type="secondary" style={{ marginLeft: 'var(--space-2)' }}>
            {isAffiliate
              ? 'ヘッダー：会社名,所属①,…,所属⑦（名前のみ・会社名は必須）'
              : 'ヘッダー：所属①,…,所属⑦（名前のみ）'}
          </Text>
          <Text
            type="secondary"
            style={{ display: 'block', marginTop: 'var(--space-2)' }}
          >
            既存組織と同じ経路・名前であれば、自動で組織の履歴が引き継がれます。サンプル行は実際の組織に置き換えてください。
          </Text>
        </div>

        {/* ※ react-dropzone 11.x の accept は文字列形式（v12+ のオブジェクト形式は不可） */}
        <Dropzone onDrop={onCsvDrop} multiple={false} accept=".csv, text/csv">
          {({ getRootProps, getInputProps }) => (
            <div
              {...getRootProps()}
              style={{
                border: '1px dashed var(--grey-30)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--grey-10)',
                padding: 'var(--space-6)',
                textAlign: 'center',
                cursor: 'pointer',
                marginBottom: 'var(--space-4)',
              }}
            >
              <input {...getInputProps()} />
              <UploadOutlined style={{ fontSize: 'var(--icon-lg)' }} />
              <div style={{ marginTop: 'var(--space-2)' }}>
                {csvFileName
                  ? `選択中：${csvFileName}`
                  : 'ここに CSV ファイルをドラッグ、またはクリックして選択'}
              </div>
            </div>
          )}
        </Dropzone>

        {versions.length > 0 && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <Text strong style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
              履歴継承の基準バージョン（任意）
            </Text>
            <Select
              allowClear
              style={{ width: '100%' }}
              placeholder="既存の組織の履歴を引き継ぐ基準バージョン"
              value={csvBaseVersionId}
              onChange={(v) => {
                // 基準バージョンを変えると承継の判定が変わるため、その場でプレビューを取り直す
                // （state 反映前のため新しい値を明示的に渡す）
                setCsvBaseVersionId(v);
                if (csvRows) runCsvPreview({ baseVersionId: v });
              }}
              options={versions.map((v) => ({
                label: `${v.name}（${fmtDate(v.effective_from)}）`,
                value: v._id,
              }))}
            />
          </div>
        )}

        {/* バージョン情報（必須）— プレビュー前から入力できるよう常時表示する */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Text
            strong
            style={{ display: 'block', marginBottom: 'var(--space-2)' }}
          >
            バージョン名（必須）
          </Text>
          <Input
            value={csvName}
            onChange={(e) => setCsvName(e.target.value)}
            placeholder="例：2026年度 組織表"
          />
        </div>
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Text
            strong
            style={{ display: 'block', marginBottom: 'var(--space-2)' }}
          >
            有効開始日（必須）
          </Text>
          <DatePicker
            style={{ width: '100%' }}
            value={csvDate}
            onChange={setCsvDate}
          />
        </div>
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Text
            strong
            style={{ display: 'block', marginBottom: 'var(--space-2)' }}
          >
            備考
          </Text>
          <Input.TextArea
            rows={2}
            value={csvNote}
            onChange={(e) => setCsvNote(e.target.value)}
          />
        </div>

        {/* プレビュー（自動実行）が失敗した場合のみ再試行を出す */}
        {csvPreviewFailed && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 'var(--space-3)' }}
            message="プレビューの取得に失敗しました"
            description={
              <Button
                size="small"
                onClick={() => runCsvPreview()}
                loading={csvSubmitting}
              >
                再試行
              </Button>
            }
          />
        )}

        {/* 表記ゆれの可能性（新規↔廃止で名称が似ている組織の警告） */}
        {csvWarnings && csvWarnings.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 'var(--space-3)' }}
            message="表記ゆれの可能性"
            description={
              <ul style={{ paddingLeft: 'var(--space-5)', margin: 0 }}>
                {csvWarnings.map((w, i) => (
                  <li key={i}>
                    ⚠ 表記ゆれの可能性: 新規『{w.new_name}』 ↔ 廃止『
                    {w.abolished && w.abolished.name}』
                    {w.abolished && w.abolished.path
                      ? `（${w.abolished.path}）`
                      : ''}
                    {w.hint ? ` — ${w.hint}` : ''}
                  </li>
                ))}
                {csvTruncated && <li>ほか多数…</li>}
              </ul>
            }
          />
        )}

        {csvPreview && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <Space wrap style={{ marginBottom: 'var(--space-3)' }}>
              <StatusBadge tone="neutral">
                合計 {csvPreview.total_nodes ?? 0}
              </StatusBadge>
              <StatusBadge tone="success">
                承継 {csvPreview.carried_count ?? 0}
              </StatusBadge>
              <StatusBadge tone="info">
                新設 {(csvPreview.added || []).length}
              </StatusBadge>
              <StatusBadge tone="warning">
                改名 {(csvPreview.renamed || []).length}
              </StatusBadge>
              <StatusBadge tone="error">
                消滅 {(csvPreview.disappeared || []).length}
              </StatusBadge>
            </Space>

            {csvPreview.errors && csvPreview.errors.length > 0 && (
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <Text strong style={{ color: 'var(--red-active)' }}>
                  エラー（{csvPreview.errors.length} 件）
                </Text>
                <Table
                  size="small"
                  rowKey={(r, i) => i}
                  pagination={false}
                  columns={[
                    { title: '行番号', dataIndex: 'row', key: 'row', width: 100 },
                    { title: '内容', dataIndex: 'message', key: 'message' },
                  ]}
                  dataSource={csvPreview.errors}
                />
              </div>
            )}

            {(!csvPreview.errors || csvPreview.errors.length === 0) && (
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <StatusBadge tone="success">エラーはありません</StatusBadge>
              </div>
            )}
          </div>
        )}

        {/* 改名（消滅↔新設のペアリング）: 消滅した組織を新設 path と 1:1 で紐付けて履歴を引き継ぐ */}
        {csvBasePreview &&
          (!csvPreview || !csvPreview.errors || csvPreview.errors.length === 0) &&
          (csvBasePreview.disappeared || []).length > 0 &&
          (csvBasePreview.added || []).length > 0 && (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Text
                strong
                style={{ display: 'block', marginBottom: 'var(--space-2)' }}
              >
                改名の指定（任意）
              </Text>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 'var(--space-3)' }}
                message="消滅と新設に同一組織（改名）が含まれる場合は、ペアリングすると組織の履歴が引き継がれます。"
              />
              <Table
                size="small"
                rowKey={(r) => r.lineage_id}
                pagination={false}
                dataSource={csvBasePreview.disappeared || []}
                columns={[
                  {
                    title: '消滅した組織',
                    key: 'old',
                    render: (text, r) => (
                      <span>
                        {r.name}
                        {r.path ? (
                          <Text
                            type="secondary"
                            style={{ marginLeft: 'var(--space-2)' }}
                          >
                            {r.path}
                          </Text>
                        ) : null}
                      </span>
                    ),
                  },
                  {
                    title: '改名先（新設）',
                    key: 'newPath',
                    render: (text, r) => {
                      // 他の消滅行で既に選択済みの path は 1:1 のため選べない
                      const selectedElsewhere = new Set(
                        Object.entries(csvPairings)
                          .filter(([k]) => k !== r.lineage_id)
                          .map(([, v]) => v)
                          .filter(Boolean)
                      );
                      const suggestion = (
                        (csvPreview && csvPreview.rename_suggestions) ||
                        []
                      ).find((s) => s.old_lineage_id === r.lineage_id);
                      return (
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Select
                            allowClear
                            style={{ width: '100%' }}
                            placeholder="改名先を選択（任意）"
                            value={csvPairings[r.lineage_id]}
                            onChange={(val) => setPairing(r.lineage_id, val)}
                            options={(csvBasePreview.added || []).map((a) => ({
                              label: a.path,
                              value: a.path,
                              disabled: selectedElsewhere.has(a.path),
                            }))}
                          />
                          {suggestion &&
                            csvPairings[r.lineage_id] !==
                              suggestion.new_path && (
                              <Button
                                type="link"
                                size="small"
                                style={{ padding: 0 }}
                                onClick={() =>
                                  setPairing(r.lineage_id, suggestion.new_path)
                                }
                              >
                                候補: {suggestion.new_path} を採用
                              </Button>
                            )}
                        </Space>
                      );
                    },
                  },
                ]}
              />
            </div>
          )}

      </Modal>
    </PageContainer>
  );
}

export default OrgMgrPage;
